import { create } from "zustand";
import {
	fetchDeviceSharedVolumeChildren,
	fetchDeviceSharedVolumes,
	refreshDeviceSharedVolumeListing,
	scanDeviceSharedVolumes,
	type DeviceSharedVolumeListing,
	type DeviceSharedVolumes,
} from "@/lib/api";
import {
	DEVICE_SHARED_VOLUME_ACTIVITY_POLLS,
	deviceSharedVolumeContentsChanged,
	deviceSharedVolumeRowKey,
	deviceSharedVolumeTransferFinished,
	type DeviceSharedVolumeDirectory,
} from "@/lib/device-shared-volumes";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";

/** 取り直しのときに、クラウドの一覧も取り直すボリューム (もう取り直しているものは除く)。 */
type DeviceSharedVolumeRelist = "none" | "missing" | "all";

interface DeviceSharedVolumeStoreState {
	/** 今見ている環境 (デバイスの環境でなければ overview は null のまま)。 */
	environmentId: string | null;
	/** 環境を切り替えるたびに増やし、古い応答を捨てる。 */
	generation: number;
	/** null は未取得。 */
	overview: DeviceSharedVolumes | null;
	/** 読み込んだディレクトリ (キーは deviceSharedVolumeRowKey)。 */
	directories: ReadonlyMap<string, DeviceSharedVolumeDirectory>;
	loadingDirectories: ReadonlySet<string>;
	/** 手元の変更を数えているか、クラウドの一覧の取り直しを頼んでいるか (裏の取り直しは listing.refreshing)。 */
	refreshing: boolean;
	/** 反映・取り込みを頼んだ後に状態を取り直す残りの回数。 */
	activityPollsLeft: number;
	/**
	 * 反映を頼んだ後、ランナーの報告がまだ載っていないボリューム (値は頼んだときに載っていた state.updatedAt)。
	 * 載っている状態が古い間に同じボリュームの反映を重ねて頼まないようにする。
	 */
	pushRequested: ReadonlyMap<string, string | null>;
	/** 取り込み・反映を頼み、activityPollsLeft の間ランナーの報告を待っているボリューム。 */
	activityVolumes: ReadonlySet<string>;
	/** クラウドの一覧の取り直しを頼んでいる途中のボリューム (頼んだ後の裏の取り直しは listing.refreshing)。 */
	relisting: ReadonlySet<string>;
	/** 環境を切り替える。同じ環境なら何もしない。 */
	track: (environmentId: string | null) => void;
	/**
	 * 状態 (scan なら手元の変更を数え直してから) と、読み込んだディレクトリを取り直す。取った状態を返す (環境を切り替えて
	 * いれば null)。状態・一覧を取れなくても、取れた分で残りを取り直してから失敗を投げる (複数なら AggregateError)。
	 * scan は走っているものがあれば、その後に 1 回だけ走らせる scan の結果を待つ。
	 */
	refresh: (options: {
		scan: boolean;
		relist: DeviceSharedVolumeRelist;
	}) => Promise<DeviceSharedVolumes | null>;
	/** ディレクトリの直下の最初の頁を読む。同じディレクトリを読んでいる途中なら、待たずに何もしない。 */
	loadDirectory: (volumeId: string, path: string) => Promise<void>;
	/** 続きの頁を読み足す。読む間に取り直しで一覧が差し替わったら、読んだ頁は捨てる。 */
	loadMore: (volumeId: string, path: string) => Promise<void>;
	/** 読み込んだディレクトリを、前に読んだ件数まで読み直す。後に始めた読み込みの結果が載っていれば、そちらを残す。 */
	reloadDirectories: () => Promise<void>;
	/**
	 * 状態だけを取り直す (転送中・一覧の取り直し中のポーリング)。木の中身 (一覧の取り直しが終わったことも) が
	 * 変わっていればディレクトリも取り直し、ボリュームを出していて転送が終わったなら手元の変更も数え直す。同時には 1 つしか
	 * 走らせない。
	 */
	poll: () => Promise<void>;
	/** ランナーが進み具合を報告し始めるまで、ポーリングを続けさせる。 */
	expectActivity: (volumeIds: readonly string[]) => void;
	/** 反映を頼んだボリュームを、ランナーの報告が載るまで pushRequested に入れ、ポーリングを続けさせる。 */
	expectPush: (volumeIds: readonly string[]) => void;
}

const EMPTY_DIRECTORIES: ReadonlyMap<string, DeviceSharedVolumeDirectory> = new Map();
const EMPTY_PUSH_REQUESTS: ReadonlyMap<string, string | null> = new Map();
const EMPTY_VOLUME_IDS: ReadonlySet<string> = new Set();

function relistTargets(overview: DeviceSharedVolumes, relist: DeviceSharedVolumeRelist) {
	return overview.volumes.filter(
		(volume) =>
			volume.listing?.refreshing !== true &&
			(relist === "all" ||
				(relist === "missing" && (volume.listing === null || !volume.listing.complete))),
	);
}

const RELOAD_CONCURRENCY = 4;

/** 待ち終えた (activityPollsLeft が 0 の) ときに残っている分は捨てて足す。 */
function addAwaitedVolumes(
	state: Pick<DeviceSharedVolumeStoreState, "activityPollsLeft" | "activityVolumes">,
	volumeIds: readonly string[],
): ReadonlySet<string> {
	return new Set([...(state.activityPollsLeft > 0 ? state.activityVolumes : []), ...volumeIds]);
}

/** 前の頁の件数に届くまで頁を読む (続きを開いていたディレクトリが縮まないように)。 */
async function fetchDirectoryPages(
	environmentId: string,
	volumeId: string,
	path: string,
	minimumEntries: number,
): Promise<DeviceSharedVolumeDirectory> {
	const entries: DeviceSharedVolumeDirectory["entries"] = [];
	let cursor: string | null = null;
	do {
		const page = await fetchDeviceSharedVolumeChildren(environmentId, volumeId, path, cursor);
		entries.push(...page.entries);
		cursor = page.nextCursor;
	} while (cursor !== null && entries.length < minimumEntries);
	return { entries, nextCursor: cursor };
}

function volumeIdOfKey(key: string): string {
	return key.split("/", 1)[0];
}

function withoutKey(set: ReadonlySet<string>, key: string): ReadonlySet<string> {
	const next = new Set(set);
	next.delete(key);
	return next;
}

/** 1 つなら元のエラーのまま、複数なら AggregateError にまとめる。 */
function combineFailures(failures: readonly unknown[]): unknown {
	return failures.length === 1
		? failures[0]
		: new AggregateError(failures, "共有ボリュームの取り直しに失敗しました。");
}

/** 頼んだ後にランナーが報告した (state.updatedAt が変わった) ボリュームと、外れたボリュームを除く。 */
function awaitingPushReport(
	requested: ReadonlyMap<string, string | null>,
	overview: DeviceSharedVolumes,
): ReadonlyMap<string, string | null> {
	if (requested.size === 0) return requested;
	const next = new Map<string, string | null>();
	for (const volume of overview.volumes) {
		const updatedAt = requested.get(volume.volumeId);
		if (updatedAt !== undefined && volume.state.updatedAt === updatedAt) {
			next.set(volume.volumeId, updatedAt);
		}
	}
	return next.size === requested.size ? requested : next;
}

interface OverviewResponse {
	/** 応答の通し番号 (大きいほど新しい)。 */
	request: number;
	overview: DeviceSharedVolumes;
}

interface PendingScan {
	generation: number;
	promise: Promise<OverviewResponse>;
}

/** 付いているボリュームがあるか。 */
export function hasVolumes(overview: DeviceSharedVolumes | null): boolean {
	return (overview?.volumes.length ?? 0) > 0;
}

/**
 * ファイルのツリーの共有ボリュームのデータ (デバイスの環境の写し)。チャットのストアとは別に持ち、
 * hook (useDeviceSharedVolumes) とターンの終わり・付け外しの後の取り直しから使う。
 */
export const useDeviceSharedVolumeStore = create<DeviceSharedVolumeStoreState>((set, get) => {
	/** 頼んだときの環境のまま応答を受けてよいか。 */
	const current = (generation: number) => get().generation === generation;
	/**
	 * 状態の応答の通し番号。GET と一覧の取り直しは頼んだとき、scan は応答を受けたときに振る (scan の途中に頼んだ GET は、
	 * scan が数えた結果を保存する前の状態を返しうる)。大きい番号の応答を載せていれば、小さい番号の応答は捨てる。
	 */
	let overviewRequests = 0;
	let appliedOverviewRequest = 0;
	/** ディレクトリの読み込み・取り直しの通し番号と、キーごとに載っている結果の番号。後に始めた読み込みの結果だけを載せる。 */
	let directoryLoads = 0;
	let directoryLoadOf = new Map<string, number>();
	let refreshesInFlight = 0;
	let pollInFlight: Promise<void> | null = null;
	let scanInFlight: PendingScan | null = null;
	/** scan が走っている間に頼まれた scan (その後に 1 回だけ走らせる)。 */
	let scanQueued: PendingScan | null = null;

	const startScan = (environmentId: string, generation: number) => {
		const promise = scanDeviceSharedVolumes(environmentId).then(
			(overview): OverviewResponse => ({ request: ++overviewRequests, overview }),
		);
		const pending = { generation, promise };
		scanInFlight = pending;
		const settle = () => {
			if (scanInFlight === pending) scanInFlight = null;
		};
		// 失敗は scan を待つ呼び出し元が受ける。ここは走っている印を外すだけ。
		promise.then(settle, settle);
		return promise;
	};

	const scanOverview = (environmentId: string, generation: number): Promise<OverviewResponse> => {
		if (scanQueued?.generation === generation) return scanQueued.promise;
		const running = scanInFlight?.generation === generation ? scanInFlight.promise : null;
		if (running === null) return startScan(environmentId, generation);
		const next = () => {
			scanQueued = null;
			return current(generation) ? startScan(environmentId, generation) : running;
		};
		const promise = running.then(next, next);
		scanQueued = { generation, promise };
		return promise;
	};

	const fetchOverview = async (
		environmentId: string,
		generation: number,
		scan: boolean,
	): Promise<OverviewResponse> => {
		if (scan) return scanOverview(environmentId, generation);
		const request = ++overviewRequests;
		return { request, overview: await fetchDeviceSharedVolumes(environmentId) };
	};

	/** 古い応答でなければ載せる。載せたかを返す。 */
	const applyOverview = (generation: number, request: number, overview: DeviceSharedVolumes) => {
		if (!current(generation) || request < appliedOverviewRequest) return false;
		appliedOverviewRequest = request;
		set((state) => ({
			overview,
			pushRequested: awaitingPushReport(state.pushRequested, overview),
		}));
		return true;
	};

	const runPoll = async () => {
		const { environmentId, generation, overview: previous } = get();
		if (environmentId === null) return;
		set((state) => ({ activityPollsLeft: Math.max(0, state.activityPollsLeft - 1) }));
		const { request, overview } = await fetchOverview(environmentId, generation, false);
		if (!applyOverview(generation, request, overview) || !previous) return;
		if (!deviceSharedVolumeContentsChanged(previous.volumes, overview.volumes)) return;
		if (
			deviceSharedVolumeTransferFinished(previous.volumes, overview.volumes) &&
			hasVolumes(overview)
		) {
			await get().refresh({ scan: true, relist: "none" });
		} else {
			await get().reloadDirectories();
		}
	};

	/** 一覧を取り直すボリュームの取り直しを頼み、返った一覧を載せる。頼めなかったものの失敗を返す。 */
	const relistVolumes = async (
		environmentId: string,
		generation: number,
		overview: DeviceSharedVolumes,
		relist: DeviceSharedVolumeRelist,
	): Promise<unknown[]> => {
		const stale = relistTargets(overview, relist);
		if (stale.length === 0) return [];
		// 頼む前に頼んだ状態の応答は裏の取り直しが始まる前のものなので、後から届いても載せない。
		const relistRequest = ++overviewRequests;
		const results = await Promise.allSettled(
			stale.map((volume) => refreshDeviceSharedVolumeListing(environmentId, volume.volumeId)),
		);
		if (!current(generation)) return [];
		const relisted = new Map<string, DeviceSharedVolumeListing>();
		const failures: unknown[] = [];
		for (const [index, result] of results.entries()) {
			if (result.status === "fulfilled") relisted.set(stale[index].volumeId, result.value);
			else failures.push(result.reason);
		}
		if (relisted.size > 0 && relistRequest > appliedOverviewRequest) {
			appliedOverviewRequest = relistRequest;
			set((state) =>
				state.overview === null
					? state
					: {
							overview: {
								...state.overview,
								volumes: state.overview.volumes.map((volume) => {
									const listing = relisted.get(volume.volumeId);
									return listing ? { ...volume, listing } : volume;
								}),
							},
						},
			);
		}
		return failures;
	};

	return {
		environmentId: null,
		generation: 0,
		overview: null,
		directories: EMPTY_DIRECTORIES,
		loadingDirectories: new Set(),
		refreshing: false,
		activityPollsLeft: 0,
		pushRequested: EMPTY_PUSH_REQUESTS,
		activityVolumes: EMPTY_VOLUME_IDS,
		relisting: EMPTY_VOLUME_IDS,
		track: (environmentId) => {
			if (get().environmentId === environmentId) return;
			refreshesInFlight = 0;
			pollInFlight = null;
			scanInFlight = null;
			scanQueued = null;
			directoryLoadOf = new Map();
			set((state) => ({
				environmentId,
				generation: state.generation + 1,
				overview: null,
				directories: EMPTY_DIRECTORIES,
				loadingDirectories: new Set(),
				refreshing: false,
				activityPollsLeft: 0,
				pushRequested: EMPTY_PUSH_REQUESTS,
				activityVolumes: EMPTY_VOLUME_IDS,
				relisting: EMPTY_VOLUME_IDS,
			}));
		},
		refresh: async ({ scan, relist }) => {
			const { environmentId, generation } = get();
			if (environmentId === null) return null;
			const showsSpinner = scan || relist !== "none";
			if (showsSpinner) {
				refreshesInFlight++;
				set({ refreshing: true });
			}
			const before = get().overview;
			const relisted =
				before === null ? [] : relistTargets(before, relist).map((volume) => volume.volumeId);
			if (relisted.length > 0) {
				set((state) => ({ relisting: new Set([...state.relisting, ...relisted]) }));
			}
			const failures: unknown[] = [];
			try {
				let overview: DeviceSharedVolumes | null = null;
				try {
					const response = await fetchOverview(environmentId, generation, scan);
					applyOverview(generation, response.request, response.overview);
					overview = response.overview;
				} catch (err) {
					// 数え直せなくても、知っている状態で一覧と木は取り直す (「更新」で一覧を取り直せるように)。失敗は最後に投げる。
					failures.push(err);
				}
				if (!current(generation)) return null;
				const known = overview ?? get().overview;
				if (known !== null) {
					failures.push(...(await relistVolumes(environmentId, generation, known, relist)));
					if (!current(generation)) return null;
					try {
						await get().reloadDirectories();
					} catch (err) {
						failures.push(err);
					}
				}
				if (failures.length > 0) throw combineFailures(failures);
				return current(generation) ? overview : null;
			} finally {
				if (showsSpinner && current(generation)) {
					refreshesInFlight--;
					set({ refreshing: refreshesInFlight > 0 });
				}
				if (relisted.length > 0 && current(generation)) {
					set((state) => ({
						relisting: new Set([...state.relisting].filter((id) => !relisted.includes(id))),
					}));
				}
			}
		},
		loadDirectory: async (volumeId, path) => {
			const { environmentId, generation, loadingDirectories } = get();
			const key = deviceSharedVolumeRowKey(volumeId, path);
			if (environmentId === null || loadingDirectories.has(key)) return;
			const load = ++directoryLoads;
			set({ loadingDirectories: new Set(loadingDirectories).add(key) });
			try {
				const directory = await fetchDirectoryPages(environmentId, volumeId, path, 0);
				if (!current(generation) || load < (directoryLoadOf.get(key) ?? 0)) return;
				directoryLoadOf.set(key, load);
				set((state) => ({ directories: new Map(state.directories).set(key, directory) }));
			} finally {
				if (current(generation)) {
					set((state) => ({ loadingDirectories: withoutKey(state.loadingDirectories, key) }));
				}
			}
		},
		loadMore: async (volumeId, path) => {
			const { environmentId, generation, loadingDirectories, directories } = get();
			const key = deviceSharedVolumeRowKey(volumeId, path);
			const loaded = directories.get(key);
			if (environmentId === null || !loaded?.nextCursor || loadingDirectories.has(key)) return;
			set({ loadingDirectories: new Set(loadingDirectories).add(key) });
			try {
				const page = await fetchDeviceSharedVolumeChildren(
					environmentId,
					volumeId,
					path,
					loaded.nextCursor,
				);
				if (!current(generation)) return;
				// 取り直しで差し替わった一覧には、前の一覧の cursor の頁をつながない。
				set((state) =>
					state.directories.get(key) === loaded
						? {
								directories: new Map(state.directories).set(key, {
									entries: [...loaded.entries, ...page.entries],
									nextCursor: page.nextCursor,
								}),
							}
						: {},
				);
			} finally {
				if (current(generation)) {
					set((state) => ({ loadingDirectories: withoutKey(state.loadingDirectories, key) }));
				}
			}
		},
		// 載っている結果より後に始めた取り直しの結果だけを載せる (重なった取り直しの、先に始めた方の結果が後から届いても)。
		reloadDirectories: async () => {
			const { environmentId, generation, directories, overview } = get();
			if (environmentId === null || overview === null) return;
			const load = ++directoryLoads;
			const attached = new Set(overview.volumes.map((volume) => volume.volumeId));
			const targets = [...directories].filter(([key]) => attached.has(volumeIdOfKey(key)));
			const reloaded = new Map<string, DeviceSharedVolumeDirectory>();
			let cursor = 0;
			await Promise.all(
				Array.from({ length: Math.min(RELOAD_CONCURRENCY, targets.length) }, async () => {
					while (cursor < targets.length) {
						const [key, previous] = targets[cursor++];
						const volumeId = volumeIdOfKey(key);
						const path = key === volumeId ? "" : key.slice(volumeId.length + 1);
						reloaded.set(
							key,
							await fetchDirectoryPages(environmentId, volumeId, path, previous.entries.length),
						);
					}
				}),
			);
			if (!current(generation)) return;
			const state = get();
			const latest = state.overview ?? overview;
			const kept = new Set(latest.volumes.map((volume) => volume.volumeId));
			const next = new Map<string, DeviceSharedVolumeDirectory>();
			for (const [key, directory] of state.directories) {
				if (!kept.has(volumeIdOfKey(key))) continue;
				const fresh = reloaded.get(key);
				if (fresh && load > (directoryLoadOf.get(key) ?? 0)) {
					directoryLoadOf.set(key, load);
					next.set(key, fresh);
				} else {
					next.set(key, directory);
				}
			}
			set({ directories: next });
		},
		poll: () => {
			if (pollInFlight) return pollInFlight;
			const run = runPoll().finally(() => {
				if (pollInFlight === run) pollInFlight = null;
			});
			pollInFlight = run;
			return run;
		},
		expectActivity: (volumeIds) =>
			set((state) => ({
				activityPollsLeft: DEVICE_SHARED_VOLUME_ACTIVITY_POLLS,
				activityVolumes: addAwaitedVolumes(state, volumeIds),
			})),
		expectPush: (volumeIds) =>
			set((state) => {
				const pushRequested = new Map(state.pushRequested);
				for (const volume of state.overview?.volumes ?? []) {
					if (volumeIds.includes(volume.volumeId)) {
						pushRequested.set(volume.volumeId, volume.state.updatedAt);
					}
				}
				return {
					activityPollsLeft: DEVICE_SHARED_VOLUME_ACTIVITY_POLLS,
					activityVolumes: addAwaitedVolumes(state, volumeIds),
					pushRequested,
				};
			}),
	};
});

/**
 * ツリーに出している環境なら状態とディレクトリを取り直す (ターンの終わり・付け外しの後)。ボリュームを出していれば
 * 手元の変更も数え直す。付けたことで出ることもあるので、ボリュームが無くても取り直す。
 */
export function refreshDeviceSharedVolumes(environmentId: string): void {
	const store = useDeviceSharedVolumeStore.getState();
	if (store.environmentId !== environmentId || store.overview === null) return;
	reportSharedVolumeFailure(
		store.refresh({ scan: hasVolumes(store.overview), relist: "none" }),
		"共有ボリュームの状態を取り直せませんでした。",
	);
}

/** 一覧を取り直す (右クリックの「一覧を取り直す」・ファイルのツリーの再読み込み)。ボリュームを出していれば手元の変更も数え直す。 */
export function relistDeviceSharedVolumes(): void {
	const store = useDeviceSharedVolumeStore.getState();
	if (store.overview === null) return;
	reportSharedVolumeFailure(
		store.refresh({ scan: hasVolumes(store.overview), relist: "all" }),
		"共有ボリュームを更新できませんでした。",
	);
}
